/**
 * Standard installation requirements. The installation price covers a standard
 * installation only when all of them are confirmed with the client.
 */

export type RequirementKey =
  | "req_photos"
  | "req_drain"
  | "req_space"
  | "req_power";
export type RequirementValue = "ok" | "client" | "ko";
export type RequirementsStatus =
  | "pending"
  | "to_review"
  | "approved"
  | "not_standard";

export type RequirementsRecord = Partial<
  Record<RequirementKey, RequirementValue | string | null>
> & {
  req_approved_at?: string | null;
};

export interface RequirementItem {
  key: RequirementKey;
  label: string;
  hint: string;
  options: { value: RequirementValue; label: string }[];
}

export const REQUIREMENT_ITEMS: RequirementItem[] = [
  {
    key: "req_photos",
    label: "Fotos de la cocina",
    hint: "Debajo del fregadero abierto (tuberías y desagüe) y el fregadero desde arriba",
    options: [
      { value: "ok", label: "Recibidas y bien" },
      { value: "ko", label: "No valen" },
    ],
  },
  {
    key: "req_drain",
    label: "Desagüe bajo el fregadero",
    hint: "El equipo tira el agua de rechazo al desagüe",
    options: [
      { value: "ok", label: "Sí" },
      { value: "client", label: "Lo prepara el cliente" },
      { value: "ko", label: "No" },
    ],
  },
  {
    key: "req_space",
    label: "El equipo cabe bajo el fregadero",
    hint: "Necesita unos 33 × 12,5 × 37,5 cm libres",
    options: [
      { value: "ok", label: "Sí" },
      { value: "client", label: "Hará hueco" },
      { value: "ko", label: "No" },
    ],
  },
  {
    key: "req_power",
    label: "Enchufe bajo el fregadero",
    hint: "Tiene que estar puesto antes de que llegue el técnico",
    options: [
      { value: "ok", label: "Sí" },
      { value: "client", label: "Lo pondrá antes" },
      { value: "ko", label: "No" },
    ],
  },
];

/**
 * Lift in the building: only information for the technician (the unit weighs
 * 9-11 kg). It does not count for the standard installation.
 */
export type ElevatorValue = "yes" | "no" | "ground";

export const ELEVATOR_ITEM = {
  key: "req_elevator" as const,
  label: "¿Dispone de ascensor?",
  hint: "Solo informativo para el técnico: no cuenta para la instalación estándar",
  options: [
    { value: "yes" as ElevatorValue, label: "Sí" },
    { value: "no" as ElevatorValue, label: "No" },
    { value: "ground" as ElevatorValue, label: "Planta baja o casa" },
  ],
};

export const elevatorAnswer = (value?: string | null): string =>
  ELEVATOR_ITEM.options.find((option) => option.value === value)?.label ??
  "Sin confirmar";

/** Same rule as gula_requirements_status() in the database. */
export const requirementsStatus = (
  record: RequirementsRecord,
): RequirementsStatus => {
  const values = REQUIREMENT_ITEMS.map((item) => record[item.key] ?? null);
  if (values.includes("ko")) return "not_standard";
  if (values.includes(null)) return "pending";
  return record.req_approved_at ? "approved" : "to_review";
};

export const answeredCount = (record: RequirementsRecord) =>
  REQUIREMENT_ITEMS.filter(
    (item) => record[item.key] && record[item.key] !== "ko",
  ).length;

export const REQUIREMENTS_STATUS_LABEL: Record<RequirementsStatus, string> = {
  pending: "Requisitos por confirmar",
  to_review: "Requisitos por revisar",
  approved: "Instalación estándar aprobada",
  not_standard: "No es instalación estándar",
};

/** "Sí", "Lo pondrá antes"... for a stored value. */
export const requirementAnswer = (
  item: RequirementItem,
  value?: string | null,
): string =>
  item.options.find((option) => option.value === value)?.label ??
  "Sin confirmar";

/** WhatsApp text the setter sends to ask for the photos and confirm the rest. */
export const buildRequirementsMessage = (
  clientFirstName?: string | null,
  agentFirstName?: string | null,
) =>
  [
    `Hola${clientFirstName ? ` ${clientFirstName}` : ""}, ${agentFirstName ? `soy ${agentFirstName} ` : "te escribimos "}de Gula de Agua.`,
    "Para dejar lista la instalación de tu equipo de ósmosis necesitamos confirmar unas cosas:",
    "1. Fotos de debajo del fregadero, con el mueble abierto y que se vean las tuberías, y otra del fregadero desde arriba.",
    "2. Que hay desagüe debajo del fregadero.",
    "3. Que hay sitio para el equipo: unos 33 × 12,5 × 37,5 cm.",
    "4. Que hay un enchufe debajo del fregadero. Si no lo hay, tiene que estar puesto antes de que vaya el técnico.",
    "5. Si vives en un piso, ¿el edificio tiene ascensor?",
    "Con todo esto confirmado, la instalación está incluida en el precio. ¡Gracias!",
  ].join("\n");

/** wa.me link for a Spanish phone number (adds 34 to 9-digit numbers). */
export const whatsappUrl = (phone: string, text: string): string | null => {
  let digits = phone.replace(/\D/g, "").replace(/^00/, "");
  if (digits.length === 9) digits = `34${digits}`;
  if (digits.length < 10) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
};
